import { useState } from "react";
import { Box, Palette, Stack, Typography } from "@mui/material";
import { ChevronRight, ChevronUp, Code2 } from "lucide-react";
import { IChatBlock } from "@pages/askDost/types";
import { borderGeneralStyle } from "@utils/cssStyles";
import {
  codeBlockHeaderStyle,
  codeBlockBodyStyle,
  codeLineNumberStyle,
  codeLineTextStyle,
  codeOutputLabelStyle,
  codeOutputTextStyle,
} from "../../styles/CodeBlockStyle";

interface ICodeBlockProps {
  block: IChatBlock;
  palette: Palette;
  translate: (text: string, options?: any) => string;
}

export const CodeBlock: React.FC<ICodeBlockProps> = ({
  block,
  palette,
  translate,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const code = block?.code;

  if (!code) return null;

  const lines = code?.lines ?? [];

  const isResolved =
    code?.stdout !== undefined ||
    code?.stderr !== undefined ||
    code?.return_code !== undefined;
  const label = translate(isResolved ? "code_executed" : "executing_code");

  return (
    <Box
      sx={{
        ...borderGeneralStyle,
        borderRadius: 1.5,
        overflow: "hidden",
        mb: 1,
      }}
    >
      {/* HEADER */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        onClick={() => setIsOpen(!isOpen)}
        sx={codeBlockHeaderStyle(palette)}
      >
        <Code2 size={16} color={palette.secondary.dark} />
        <Typography
          variant="body2"
          sx={{ flex: 1, fontSize: 12, color: palette.text.primary }}
        >
          {label}
        </Typography>
        {isOpen ? <ChevronUp size={16} /> : <ChevronRight size={16} />}
      </Stack>

      {/* BODY */}
      {isOpen && (
        <Box sx={codeBlockBodyStyle(palette)}>
          {lines?.map((line, index) => (
            <Stack key={index} direction="row" spacing={1.5}>
              <Typography component="span" sx={codeLineNumberStyle(palette)}>
                {index + 1}
              </Typography>
              <Typography component="span" sx={codeLineTextStyle(palette)}>
                {line || " "}
              </Typography>
            </Stack>
          ))}

          {(code?.stdout || code?.stderr) && (
            <Box
              sx={{
                mt: 1,
                pt: 1,
                borderTop: `1px dashed ${palette.primary.contrastText}`,
              }}
            >
              <Typography variant="caption" sx={codeOutputLabelStyle(palette)}>
                {translate("output")}
              </Typography>
              <Typography sx={codeOutputTextStyle(palette, code?.is_error)}>
                {code?.stderr || code?.stdout}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
